import React, {
  type FocusEvent,
  type ReactElement,
  useCallback,
  useMemo,
  useState,
} from 'react';
import {
  Box,
  Icon,
  InlineMessage,
  InputField,
  Link as DSLink,
} from '@saviynt/design-system';

import { FieldLabel } from '../../../../shared/components';
import {
  SENSITIVE_FIELD_MASK,
  testIds,
  VALIDATION_TYPES,
} from '../../../../shared/constants';
import { useInvertedLocalize } from '../../../../shared/hooks';
import { type PasswordField } from '../../../../shared/types';
import { stripHtmlTags } from '../../../../shared/utils';
import { FIELD_LABEL_VALIDATION } from '../../../FormBuilder/data-layer/validation/validationRules';

import { previewFieldMessages } from './messages';

import styles from './PreviewFields.module.css';

interface PasswordProps {
  field: PasswordField;
  value?: string;
  onChange?: (value: string) => void;
  error?: string;
  isRequired?: boolean;
  confirmValue?: string;
  onConfirmChange?: (value: string) => void;
  onSentinelCleared?: (fieldName: string) => void;
}

function Password({
  field,
  value,
  onChange,
  error,
  isRequired: isRequiredProp,
  confirmValue = '',
  onConfirmChange,
  onSentinelCleared,
}: PasswordProps): ReactElement {
  const [isVisible, setIsVisible] = useState(false);
  const [isVisibleConfirm, setIsVisibleConfirm] = useState(false);
  const [regexError, setRegexError] = useState('');

  const l = useInvertedLocalize(previewFieldMessages);

  const { fieldName, label, infoTooltipText, advConfig, validations } = field;

  const isSentinel = value === SENSITIVE_FIELD_MASK;

  const isPasswordConfirmation = advConfig?.isPasswordConfirmation ?? false;
  const isAutoGenerationEnabled = advConfig?.isAutoGenerationEnabled ?? false;

  const handleFocus = useCallback(
    (e: FocusEvent<HTMLDivElement>) => {
      if (isSentinel && e.target instanceof HTMLInputElement) {
        onChange?.('');
        onSentinelCleared?.(fieldName);
      }
    },
    [isSentinel, onChange, onSentinelCleared, fieldName]
  );

  const showConfirmation = useMemo(
    () => !isSentinel && isPasswordConfirmation,
    [isSentinel, isPasswordConfirmation]
  );

  const showAutoGenerate = useMemo(
    () => !isSentinel && isAutoGenerationEnabled,
    [isSentinel, isAutoGenerationEnabled]
  );

  // Use prop if provided, otherwise fallback to validation rules
  const isRequired =
    isRequiredProp ??
    validations?.some((rule) => rule.type === VALIDATION_TYPES.REQUIRED) ??
    false;

  const regexValidation = validations?.find(
    (v) => v.type === VALIDATION_TYPES.REGEX
  );

  // TODO: Modern browsers have broken autocomplete behavior with type='password'
  // Note: Masked editing only supports append/delete at end. Use visibility toggle for mid-string edits.
  // Mask password values with bullet points when not visible
  const displayValue = isVisible
    ? (value ?? '')
    : '•'.repeat((value ?? '').length);
  const displayConfirmValue = isVisibleConfirm
    ? confirmValue
    : '•'.repeat(confirmValue.length);

  const getConfirmError = (): string => {
    if (!isPasswordConfirmation || !value) {
      return '';
    }

    if (!confirmValue) {
      return l('Confirm password is required');
    }

    if (confirmValue !== value) {
      return l('Passwords do not match');
    }

    return '';
  };

  const confirmError = getConfirmError();

  const generatePassword = (regex: string): string => {
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const digits = '0123456789';
    const symbols = '!@#$%^&*()_+-=[]{}|;:,.<>?';

    if (!regex) {
      // Default password: 12 chars with lowercase, uppercase, digit, symbol
      let password = '';

      password += lowercase[Math.floor(Math.random() * lowercase.length)];
      password += uppercase[Math.floor(Math.random() * uppercase.length)];
      password += digits[Math.floor(Math.random() * digits.length)];
      password += symbols[Math.floor(Math.random() * symbols.length)];

      const allChars = lowercase + uppercase + digits + symbols;

      for (let i = 4; i < 12; i += 1) {
        password += allChars[Math.floor(Math.random() * allChars.length)];
      }

      return password
        .split('')
        .sort(() => Math.random() - 0.5)
        .join('');
    }

    // Generate password matching regex
    let attempts = 0;
    const maxAttempts = 100;

    while (attempts < maxAttempts) {
      let password = '';

      // Ensure requirements: lowercase, uppercase, digit, symbol
      password += lowercase[Math.floor(Math.random() * lowercase.length)];
      password += uppercase[Math.floor(Math.random() * uppercase.length)];
      password += digits[Math.floor(Math.random() * digits.length)];
      password += symbols[Math.floor(Math.random() * symbols.length)];

      // Fill to 12+ characters
      const allChars = lowercase + uppercase + digits + symbols;
      const length = Math.max(12, Math.floor(Math.random() * 4) + 12);

      for (let i = 4; i < length; i += 1) {
        password += allChars[Math.floor(Math.random() * allChars.length)];
      }

      password = password
        .split('')
        .sort(() => Math.random() - 0.5)
        .join('');

      try {
        const regexPattern = new RegExp(regex);

        if (regexPattern.test(password)) {
          return password;
        }
      } catch {
        break;
      }

      attempts += 1;
    }

    return '';
  };

  const validateRegex = (password: string) => {
    if (!regexValidation || !password) {
      setRegexError('');

      return;
    }

    try {
      const regex = new RegExp(regexValidation.value);

      if (!regex.test(password)) {
        setRegexError(
          stripHtmlTags(regexValidation.message || 'Invalid password format')
        );
      } else {
        setRegexError('');
      }
    } catch {
      setRegexError('');
    }
  };

  const handleGenerateForMe = () => {
    const regex = regexValidation?.value || '';
    const generatedPassword = generatePassword(regex);

    onChange?.(generatedPassword);

    if (isPasswordConfirmation) {
      onConfirmChange?.(generatedPassword);
    }

    validateRegex(generatedPassword);
  };

  // Intercept copy so the real value is placed on the clipboard, not bullet chars.
  // When sentinel, suppress copy entirely (sentinel is not the real value).
  const handleCopyPassword = useCallback(
    (e: React.ClipboardEvent<HTMLDivElement>) => {
      if (isSentinel) {
        e.preventDefault();

        return;
      }

      if (!isVisible && value) {
        e.clipboardData.setData('text/plain', value);
        e.preventDefault();
      }
    },
    [isVisible, isSentinel, value]
  );

  const handleCopyConfirm = useCallback(
    (e: React.ClipboardEvent<HTMLDivElement>) => {
      if (!isVisibleConfirm && confirmValue) {
        e.clipboardData.setData('text/plain', confirmValue);
        e.preventDefault();
      }
    },
    [isVisibleConfirm, confirmValue]
  );

  return (
    <Box className={styles.password}>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        onCopy={handleCopyPassword}
        onFocus={handleFocus}
        className={styles.passwordFieldWrapper}>
        <InputField
          id={fieldName}
          name={fieldName}
          size='medium'
          autoComplete='new-password'
          placeholder=''
          value={displayValue}
          setValue={(v: string) => {
            if (isVisible) {
              // When visible, update normally
              onChange?.(v);
              validateRegex(v);
            } else {
              // When masked, calculate actual value from input changes
              const currentLength = (value ?? '').length;
              const newLength = v.length;

              if (newLength > currentLength) {
                // Characters added (skip bullet points)
                const addedChars = v.slice(currentLength).replace(/•/g, '');
                const newValue = (value ?? '') + addedChars;

                onChange?.(newValue);
                validateRegex(newValue);
              } else if (newLength < currentLength) {
                // Characters removed
                const newValue = (value ?? '').slice(0, newLength);

                onChange?.(newValue);
                validateRegex(newValue);
              }
            }
          }}
          label={
            <FieldLabel
              label={label.slice(0, FIELD_LABEL_VALIDATION.MAX_LENGTH)}
              htmlFor={fieldName}
              isReadOnly={false}
              infoTooltipText={infoTooltipText}
            />
          }
          suffixButtonType='password'
          suffixIsToggle={isVisible}
          suffixOnClick={() => setIsVisible(!isVisible)}
          isRequired={isRequired}
          tabIndex={0}
          isCritical={!!(error || regexError)}
          CriticalHelperText={
            <Box>
              <InlineMessage
                text={regexError || error}
                colorTheme='critical'
                size='small'
                leftIcon={<Icon kind='AlertCriticalFilled' />}
              />
              {showAutoGenerate && (
                <DSLink
                  kind='button'
                  text={l('Generate for me')}
                  onClick={handleGenerateForMe}
                  tabIndex={0}
                  dataTestId={testIds.FF_PREVIEW_PASSWORD_GENERATE}
                />
              )}
            </Box>
          }
          HelperText={
            showAutoGenerate ? (
              <DSLink
                kind='button'
                text={l('Generate for me')}
                onClick={handleGenerateForMe}
                tabIndex={0}
                dataTestId={testIds.FF_PREVIEW_PASSWORD_GENERATE}
              />
            ) : undefined
          }
          shouldDisallowBrackets={false}
          dataTestId={testIds.FF_PREVIEW_PASSWORD_FIELD}
        />
      </div>
      {showConfirmation && (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div onCopy={handleCopyConfirm} className={styles.passwordFieldWrapper}>
          <InputField
            id={`confirm-${fieldName}`}
            name={`confirm-${fieldName}`}
            size='medium'
            autoComplete='new-password'
            placeholder={l('Type again to confirm')}
            value={displayConfirmValue}
            setValue={(v: string) => {
              if (isVisibleConfirm) {
                // When visible, update normally
                onConfirmChange?.(v);
              } else {
                // When masked, calculate actual value from input changes
                const currentLength = confirmValue.length;
                const newLength = v.length;

                if (newLength > currentLength) {
                  // Characters added (skip bullet points)
                  const addedChars = v.slice(currentLength).replace(/•/g, '');

                  onConfirmChange?.(confirmValue + addedChars);
                } else if (newLength < currentLength) {
                  // Characters removed
                  onConfirmChange?.(confirmValue.slice(0, newLength));
                }
              }
            }}
            suffixButtonType='password'
            suffixIsToggle={isVisibleConfirm}
            suffixOnClick={() => setIsVisibleConfirm(!isVisibleConfirm)}
            label={
              <FieldLabel
                label={`${l('Confirm')} ${label}`}
                htmlFor={`confirm-${fieldName}`}
                isReadOnly={false}
              />
            }
            isRequired={isRequired}
            tabIndex={0}
            isCritical={!!confirmError}
            CriticalHelperText={
              <InlineMessage
                text={confirmError}
                colorTheme='critical'
                size='small'
                leftIcon={<Icon kind='AlertCriticalFilled' />}
              />
            }
            shouldDisallowBrackets={false}
            dataTestId={testIds.FF_PREVIEW_PASSWORD_FIELD_CONFIRM}
          />
        </div>
      )}
    </Box>
  );
}

export default Password;
